<script lang="ts">
    import { afterUpdate } from 'svelte';
    import { marked } from 'marked';
    import { Lightning as LightningIcon, Sparkle as SparkleIcon, Brain as BrainIcon, MagnifyingGlass as MagnifyingGlassIcon, TrashSimple as TrashSimpleIcon, Plus as PlusIcon, CaretDown as CaretDownIcon, X as XIcon } from 'phosphor-svelte';
    import { space, roolClient, promptInput, isPrompting, selectedNodeIds, nodes, focusNodeRequest, interactionsUpdateTrigger, canvasEditor, conversations, isChatOpen, promptHistory } from '../../stores';
    import { switchConversation, createNewConversation, refreshConversations } from '../../lib/space';
    import spinnerVideo from '../../assets/spinner.mp4';
    import logoText from '../../assets/logo-text.png';

    export let onSubmit: (prompt: string, objectIds?: string[], effort?: EffortLevel) => Promise<void>;

    // Custom renderer for links to handle object references
    const renderer = {
        link(href: string, title: string | null | undefined, text: string): string {
            const matchingNode = $nodes.find(n => n.id === href);
            
            if (matchingNode) {
                return `<a href="#" class="object-link" data-object-id="${href}">
                    <svg class="link-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                    </svg>
                    ${text}
                </a>`;
            }
            
            // Default link rendering
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}"${titleAttr}>${text}</a>`;
        }
    };

    marked.use({
        breaks: true,
        gfm: true,
        renderer: renderer
    });

    function parseActions(content: string): { text: string; actions: string[] } {
        const actions: string[] = [];
        const text = content.replace(/```actions\n([\s\S]*?)```/g, (_match, block) => {
            const lines = block.trim().split('\n').map((l: string) => l.trim()).filter((l: string) => l);
            actions.push(...lines);
            return '';
        });
        return { text: text.trim(), actions };
    }

    function renderMarkdown(content: string): string {
        return marked.parse(content, { async: false }) as string;
    }

    let inputValue = '';
    let chatContainer: HTMLDivElement;

    promptInput.subscribe(value => {
        inputValue = value;
    });

    type EffortLevel = 'QUICK' | 'STANDARD' | 'REASONING' | 'RESEARCH';
    let effort: EffortLevel = 'STANDARD';
    let isEffortOpen = false;
    const effortOptions: Array<{ value: EffortLevel; label: string; icon: typeof LightningIcon }> = [
        { value: 'QUICK', label: 'Quick', icon: LightningIcon },
        { value: 'STANDARD', label: 'Standard', icon: SparkleIcon },
        { value: 'REASONING', label: 'Reasoning', icon: BrainIcon },
        { value: 'RESEARCH', label: 'Research', icon: MagnifyingGlassIcon },
    ];
    $: currentEffort = effortOptions.find(option => option.value === effort) ?? effortOptions[1];

    let isConversationMenuOpen = false;
    let isDeleting = false;

    $: currentConversationName = (() => {
        if (!$space) return 'Chat';
        const conv = $conversations.find(c => c.id === $space?.conversationId);
        return conv?.name || 'Chat';
    })();

    $: sortedConversations = [...$conversations].sort((a, b) => b.createdAt - a.createdAt);

    function toggleConversationMenu() {
        isConversationMenuOpen = !isConversationMenuOpen;
    }

    async function handleSwitchConversation(conversationId: string) {
        isConversationMenuOpen = false;
        await switchConversation(conversationId);
    }

    async function handleNewConversation() {
        isConversationMenuOpen = false;
        await createNewConversation();
    }

    function toggleEffortMenu() {
        isEffortOpen = !isEffortOpen;
    }

    function setEffort(option: EffortLevel) {
        effort = option;
        isEffortOpen = false;
    }

    function closePanel() {
        isChatOpen.set(false);
    }

    $: selectedIds = Array.from($selectedNodeIds);
    $: selectedNodes = $nodes.filter(n => $selectedNodeIds.has(n.id));
    $: selectionBadge = (() => {
        if (selectedNodes.length === 0) return null;

        const counts: Record<string, number> = {};
        for (const n of selectedNodes) {
            const type = n.type || 'object';
            counts[type] = (counts[type] || 0) + 1;
        }

        const labels: Record<string, [string, string]> = {
            note: ['Note', 'Notes'],
            object: ['Object', 'Objects'],
            header: ['Header', 'Headers'],
            image: ['Image', 'Images'],
        };

        const parts: string[] = [];
        for (const [type, count] of Object.entries(counts)) {
            const [singular, plural] = labels[type] ?? [type, type + 's'];
            parts.push(`${count} ${count === 1 ? singular : plural}`);
        }

        return parts.join(', ');
    })();

    $: interactions = (() => {
        if (!$space) return [];
        $interactionsUpdateTrigger;

        return $space.getInteractions();
    })();

    $: chatMessages = (() => {
        const messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp: number; objectIds?: string[]; actions?: string[] }> = [];

        for (const interaction of interactions) {
            if (interaction.operation !== 'prompt') continue;

            messages.push({
                role: 'user',
                content: cleanUserInput(interaction.input),
                timestamp: interaction.timestamp,
                objectIds: interaction.modifiedObjectIds?.length > 0 ? interaction.modifiedObjectIds : undefined
            });

            if (interaction.output !== null) {
                const { text, actions } = parseActions(interaction.output);
                messages.push({
                    role: 'assistant',
                    content: text,
                    timestamp: interaction.timestamp,
                    actions: actions.length > 0 ? actions : undefined
                });
            }
        }

        return messages;
    })();

    afterUpdate(() => {
        if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
            attachPillHandlers();
        }
    });

    function attachPillHandlers() {
        if (!chatContainer) return;
        
        const links = chatContainer.querySelectorAll('.object-link');
        links.forEach(link => {
            link.removeEventListener('click', handlePillClick);
            link.addEventListener('click', handlePillClick);
        });
    }

    function handlePillClick(e: Event) {
        e.preventDefault();
        const target = e.target as HTMLElement;
        const objectId = target.dataset.objectId;

        if (objectId) {
            // Route selection through the editor so ViewState and store stay in sync
            const editor = $canvasEditor;
            if (editor) {
                editor.select([objectId]);
            } else {
                // Fallback if editor not available yet
                selectedNodeIds.set(new Set([objectId]));
            }
            focusNodeRequest.set(objectId);
        }
    }

    async function handleSubmit(e: Event) {
        e.preventDefault();
        if (!inputValue.trim() || $isPrompting) return;

        const prompt = inputValue.trim();
        const objectIds = selectedIds.length > 0 ? selectedIds : undefined;

        promptHistory.push(prompt);
        promptInput.set('');
        isPrompting.set(true);

        await onSubmit(prompt, objectIds, effort);

        isPrompting.set(false);
    }

    function handleInput(e: Event) {
        const target = e.target as HTMLTextAreaElement;
        promptInput.set(target.value);
        promptHistory.reset();
        autoResize(target);
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        } else if (e.key === 'ArrowUp') {
            const prev = promptHistory.up(inputValue);
            if (prev !== null) {
                e.preventDefault();
                promptInput.set(prev);
            }
        } else if (e.key === 'ArrowDown') {
            const next = promptHistory.down();
            if (next !== null) {
                e.preventDefault();
                promptInput.set(next);
            }
        }
    }

    function autoResize(textarea: HTMLTextAreaElement) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }

    function cleanUserInput(input: string): string {
        // This is a hack to remove the injected prompt
        const userIndex = input.indexOf('[USER]');
        if (userIndex !== -1) {
            return input.slice(userIndex + '[USER]'.length).trim();
        }
        return input;
    }

    async function clearChat() {
        if (!$space || !$roolClient || isDeleting || $isPrompting) return;
        isDeleting = true;
        try {
            const currentId = $space.conversationId;
            const spaceId = $space.id;
            await $roolClient.deleteChannel(spaceId, currentId);
            refreshConversations();

            // Switch to another conversation, or create a new one
            const remaining = $conversations.filter(c => c.id !== currentId);
            if (remaining.length > 0) {
                await switchConversation(remaining[0].id);
            } else {
                await createNewConversation();
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        } finally {
            isDeleting = false;
        }
    }

    async function submitAction(action: string) {
        if ($isPrompting) return;

        const objectIds = selectedIds.length > 0 ? selectedIds : undefined;

        promptInput.set('');
        isPrompting.set(true);

        await onSubmit(action, objectIds, effort);

        isPrompting.set(false);
    }
</script>

<div class="chat-panel">
    <div class="chat-header">
        <div class="chat-header-left">
            <div class="conversation-picker">
                <button
                    class="conversation-trigger"
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isConversationMenuOpen}
                    on:click={toggleConversationMenu}
                >
                    <span class="conversation-name">{currentConversationName}</span>
                    <CaretDownIcon size={14} />
                </button>
                {#if isConversationMenuOpen}
                    <div class="conversation-menu" role="listbox">
                        {#each sortedConversations as conv (conv.id)}
                            <button
                                class="conversation-option"
                                type="button"
                                role="option"
                                aria-selected={conv.id === $space?.conversationId}
                                on:click={() => handleSwitchConversation(conv.id)}
                            >
                                <span class="conversation-option-name">{conv.name || conv.id}</span>
                                <span class="conversation-option-count">{conv.interactionCount}</span>
                            </button>
                        {/each}
                    </div>
                {/if}
            </div>

            <!-- Effort selector in header -->
            <div class="effort-select">
                <button
                    class="effort-trigger"
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isEffortOpen}
                    on:click={toggleEffortMenu}
                >
                    <span class="effort-icon" aria-hidden="true">
                        <svelte:component this={currentEffort.icon} size={16} />
                    </span>
                    <span class="effort-label">{currentEffort.label}</span>
                </button>
                {#if isEffortOpen}
                    <div class="effort-menu" role="listbox">
                        {#each effortOptions as option}
                            <button
                                class="effort-option"
                                type="button"
                                role="option"
                                aria-selected={option.value === effort}
                                on:click={() => setEffort(option.value)}
                            >
                                <span class="effort-option-icon" aria-hidden="true">
                                    <svelte:component this={option.icon} size={16} />
                                </span>
                                <span class="effort-option-text">{option.label}</span>
                            </button>
                        {/each}
                    </div>
                {/if}
            </div>
        </div>

        <div class="chat-header-actions">
            <button
                class="chat-header-button"
                type="button"
                aria-label="New conversation"
                title="New conversation"
                on:click={handleNewConversation}
            >
                <PlusIcon size={18} />
            </button>
            <button
                class="chat-header-button"
                type="button"
                aria-label="Delete conversation"
                title="Delete conversation"
                disabled={isDeleting || $isPrompting}
                on:click={clearChat}
            >
                <TrashSimpleIcon size={18} />
            </button>
            <button
                class="chat-header-button"
                type="button"
                aria-label="Close chat"
                title="Close chat"
                on:click={closePanel}
            >
                <XIcon size={18} />
            </button>
        </div>
    </div>
    <div class="chat-messages" bind:this={chatContainer}>
        {#if chatMessages.length === 0}
            <div class="empty-state">
                <p>Start a conversation</p>
                <span class="empty-hint">Ask anything about your space</span>
            </div>
        {:else}
            {#each chatMessages as message, index (message.timestamp + index)}
                <div class="message {message.role}">
                    <div class="message-header">
                        {#if message.role === 'user'}
                            <span class="message-role">You</span>
                        {:else}
                            <img src={logoText} alt="rool" class="message-logo" />
                        {/if}
                    </div>
                    <div class="message-content">
                        {#if message.objectIds && message.objectIds.length > 0 && message.role === 'user'}
                            <div class="message-context">
                                <svg class="context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                                </svg>
                                {message.objectIds.length} object{message.objectIds.length > 1 ? 's' : ''} selected
                            </div>
                        {/if}
                        <div class="message-text markdown-content">{@html renderMarkdown(message.content)}</div>
                        {#if message.actions?.length}
                            <div class="action-buttons">
                                {#each message.actions as action}
                                    <button class="action-button" on:click={() => submitAction(action)}>
                                        <svg class="action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                        {action}
                                    </button>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            {/each}
        {/if}
    </div>

    <div class="chat-input-container">
        <form on:submit={handleSubmit} class="chat-input-form">
            {#if selectionBadge}
                <div class="selection-badge">
                    <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                    </svg>
                    {selectionBadge}
                </div>
            {/if}
            <div class="input-glow-container">
                <div class="glow-border" class:active={$isPrompting}>
                    <div class="glow-ring"></div>
                    <div class="glow-blur"></div>
                </div>
            <div class="input-wrapper" class:thinking={$isPrompting}>
                <textarea
                    value={inputValue}
                    on:input={handleInput}
                    on:keydown={handleKeydown}
                    placeholder={$isPrompting ? "Thinking..." : "Ask anything..."}
                    disabled={$isPrompting}
                    class="chat-textarea"
                    rows="1"
                ></textarea>
                <button
                    type="submit"
                    disabled={$isPrompting || !inputValue.trim()}
                    class="send-button"
                    aria-label="Send"
                >
                    {#if $isPrompting}
                        <video src={spinnerVideo} autoplay loop muted playsinline class="button-spinner"></video>
                    {:else}
                        <svg class="send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 19V5M5 12l7-7 7 7"/>
                        </svg>
                    {/if}
                </button>
            </div>
            </div>
        </form>
    </div>
</div>

<style>
    .chat-panel {
        position: relative;
        width: 100%;
        height: 100%;
        background: var(--panelSolid);
        display: flex;
        flex-direction: column;
        border-radius: 28px;
        border: 0.36px solid var(--border);
        box-shadow:
            0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
            0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .chat-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 12px 12px 16px;
        border-bottom: 0.36px solid var(--border);
        background: transparent;
    }

    .chat-header-left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .conversation-picker {
        position: relative;
        min-width: 0;
    }

    .conversation-trigger {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border-radius: 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: var(--text);
        font-size: 0.95rem;
        font-weight: 600;
        letter-spacing: 0.01em;
        transition: background 0.15s ease;
        max-width: 200px;
    }

    .conversation-trigger:hover {
        background: var(--assistantBubble);
    }

    .conversation-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .conversation-menu {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        background: var(--panelSolid);
        border: 0.36px solid var(--border);
        border-radius: 12px;
        padding: 6px;
        min-width: 220px;
        max-height: 300px;
        overflow-y: auto;
        box-shadow:
            0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
            0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);
        z-index: 10;
    }

    .conversation-option {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 10px;
        border: none;
        background: transparent;
        border-radius: 8px;
        cursor: pointer;
        color: var(--text);
        font-size: 0.85rem;
        text-align: left;
    }

    .conversation-option:hover {
        background: var(--assistantBubble);
    }

    .conversation-option[aria-selected='true'] {
        background: rgba(46, 144, 250, 0.12);
        color: #2E90FA;
    }

    .conversation-option-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }

    .conversation-option-count {
        font-size: 0.75rem;
        color: var(--muted);
        flex-shrink: 0;
    }

    /* Effort selector — in header, opens downward */
    .effort-select {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
    }

    .effort-trigger {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        border-radius: 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: var(--muted);
        font-size: 0.8rem;
        font-weight: 600;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .effort-trigger:hover {
        background: var(--assistantBubble);
        color: var(--text);
    }

    .effort-icon {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        background: rgba(46, 144, 250, 0.12);
        color: #2E90FA;
    }

    .effort-label {
        letter-spacing: 0.01em;
    }

    .effort-menu {
        position: absolute;
        top: calc(100% + 12px);
        left: 0;
        background: var(--panelSolid);
        border: 0.36px solid var(--border);
        border-radius: 12px;
        padding: 6px;
        min-width: 160px;
        box-shadow:
            0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
            0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);
        z-index: 15;
    }

    .effort-option {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border: none;
        background: transparent;
        border-radius: 8px;
        cursor: pointer;
        color: var(--text);
        font-size: 0.85rem;
        font-weight: 600;
    }

    .effort-option:hover {
        background: var(--assistantBubble);
    }

    .effort-option[aria-selected='true'] {
        background: rgba(46, 144, 250, 0.12);
        color: #2E90FA;
    }

    .effort-option-icon {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        background: rgba(46, 144, 250, 0.12);
        color: #2E90FA;
        flex-shrink: 0;
    }

    .chat-header-actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
    }

    .chat-header-button {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        border: none;
        background: transparent;
        color: var(--muted);
        display: grid;
        place-items: center;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .chat-header-button:hover {
        background: var(--assistantBubble);
        color: var(--text);
    }

    .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    .chat-messages::-webkit-scrollbar {
        width: 6px;
    }

    .chat-messages::-webkit-scrollbar-track {
        background: transparent;
    }

    .chat-messages::-webkit-scrollbar-thumb {
        background: var(--faint);
        border-radius: 999px;
        border: 2px solid transparent;
        background-clip: content-box;
        transition: background 0.2s ease;
    }

    .chat-messages::-webkit-scrollbar-thumb:hover {
        background: var(--muted);
        background-clip: content-box;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--muted);
        text-align: center;
        padding: 40px;
    }

    .empty-state p {
        margin: 0 0 8px 0;
        font-size: 1.1rem;
        font-weight: 500;
        color: var(--text);
    }

    .empty-hint {
        font-size: 0.875rem;
    }

    .message {
        display: flex;
        flex-direction: column;
        gap: 6px;
        animation: messageSlideIn 0.3s ease-out;
        min-width: 0;
        max-width: 100%;
    }

    .message.user {
        align-items: flex-end;
    }

    @keyframes messageSlideIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
    }

    .message-role {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--text);
        letter-spacing: 0.01em;
    }

    .message.user .message-role {
        color: #2E90FA;
    }

    .message.assistant .message-role {
        color: var(--muted);
    }

    .message-logo {
        height: 14px;
        width: auto;
        object-fit: contain;
    }

    .message-content {
        background: var(--assistantBubble);
        border: 0.36px solid var(--border);
        border-radius: 12px;
        padding: 12px 16px;
        line-height: 1.6;
        font-size: 0.95rem;
        min-width: 0;
        max-width: 100%;
    }

    .message.user .message-content {
        background: rgba(46, 144, 250, 0.12);
        border-color: #2E90FA;
    }

    .message.assistant .message-content {
        background: transparent;
        border-color: transparent;
    }

    .message-context {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.75rem;
        color: #2E90FA;
        background: rgba(46, 144, 250, 0.12);
        border: 0.36px solid #2E90FA;
        border-radius: 6px;
        padding: 4px 8px;
        margin-bottom: 8px;
        width: fit-content;
    }

    .context-icon {
        width: 12px;
        height: 12px;
    }

    .action-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 10px;
    }

    .action-icon {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
    }

    .action-button {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border: 0.36px solid var(--border);
        border-radius: 999px;
        background: var(--panelSolid);
        color: var(--text);
        font-size: 0.8rem;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease;
        white-space: normal;
        word-break: break-word;
        max-width: 100%;
        text-align: left;
    }

    .action-button:hover {
        background: var(--assistantBubble);
        border-color: var(--accent);
        color: var(--accent);
    }

    .action-button:active {
        transform: scale(0.97);
    }

    .message-text {
        color: var(--text);
        word-wrap: break-word;
    }

    .markdown-content :global(p) {
        margin: 0 0 0.6em 0;
    }

    .markdown-content :global(p:last-child) {
        margin-bottom: 0;
    }

    .markdown-content :global(h1),
    .markdown-content :global(h2),
    .markdown-content :global(h3),
    .markdown-content :global(h4),
    .markdown-content :global(h5),
    .markdown-content :global(h6) {
        margin: 1em 0 0.5em 0;
        font-weight: 600;
        line-height: 1.3;
        color: var(--text);
    }

    .markdown-content :global(h1) { font-size: 1.5em; }
    .markdown-content :global(h2) { font-size: 1.3em; }
    .markdown-content :global(h3) { font-size: 1.15em; }
    .markdown-content :global(h4) { font-size: 1.05em; }

    .markdown-content :global(ul),
    .markdown-content :global(ol) {
        margin: 0.5em 0;
        padding-left: 1.5em;
    }

    .markdown-content :global(li) {
        margin: 0.25em 0;
    }

    .markdown-content :global(code) {
        background: var(--assistantBubble);
        padding: 0.15em 0.4em;
        border-radius: 4px;
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.9em;
    }

    .markdown-content :global(.object-link) {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #2E90FA;
        text-decoration: none;
        font-weight: 600;
        cursor: pointer;
        transition: color 0.15s ease;
        vertical-align: top;
    }

    .markdown-content :global(.object-link .link-icon) {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        margin-bottom: -2px;
    }

    .markdown-content :global(.object-link:hover) {
        color: #1D6FD8;
    }

    .markdown-content :global(pre) {
        background: var(--assistantBubble);
        padding: 0.75em;
        border-radius: 6px;
        overflow-x: auto;
        margin: 0.75em 0;
    }

    .markdown-content :global(pre code) {
        background: none;
        padding: 0;
    }

    .markdown-content :global(blockquote) {
        border-left: 3px solid var(--border);
        padding-left: 1em;
        margin: 0.75em 0;
        color: var(--muted);
    }

    .markdown-content :global(a) {
        color: #2E90FA;
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 2px;
    }

    .markdown-content :global(a:hover) {
        color: #1D6FD8;
    }

    .markdown-content :global(hr) {
        border: none;
        border-top: 1px solid var(--border);
        margin: 1em 0;
    }

    .markdown-content :global(table) {
        border-collapse: collapse;
        width: 100%;
        margin: 0.75em 0;
    }

    .markdown-content :global(th),
    .markdown-content :global(td) {
        border: 1px solid var(--border);
        padding: 0.5em;
        text-align: left;
    }

    .markdown-content :global(th) {
        background: var(--assistantBubble);
        font-weight: 600;
    }

    .markdown-content :global(img) {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
    }

    .chat-input-container {
        padding: 16px 20px 20px;
        border-top: 0.36px solid var(--border);
        background: transparent;
    }

    .chat-input-form {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .selection-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: rgba(46, 144, 250, 0.12);
        border: 0.36px solid #2E90FA;
        border-radius: 8px;
        font-size: 0.72rem;
        font-weight: 600;
        color: #2E90FA;
        width: fit-content;
    }

    .badge-icon {
        width: 14px;
        height: 14px;
    }

    .input-glow-container {
        position: relative;
    }

    .input-wrapper {
        position: relative;
        z-index: 1;
        display: flex;
        gap: 10px;
        align-items: center;
        background: var(--bg0);
        border: 0.36px solid var(--border);
        border-radius: 28px;
        padding: 8px 12px;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .input-wrapper:focus-within {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
    }

    .input-wrapper.thinking {
        border-color: transparent;
    }

    .input-wrapper.thinking:focus-within {
        box-shadow: none;
    }

    /* Holographic glow for input */
    .glow-border {
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.4s ease;
        z-index: 0;
    }

    .glow-border.active {
        opacity: 1;
    }

    .glow-ring {
        position: absolute;
        inset: -2px;
        border-radius: 30px;
        padding: 2px;
        background: conic-gradient(
            from var(--glow-angle, 0deg),
            #a855f7,
            #6366f1,
            #06b6d4,
            #22d3ee,
            #a855f7,
            #ec4899,
            #a855f7
        );
        -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        mask-composite: exclude;
        animation: glow-spin 3s linear infinite;
    }

    .glow-blur {
        position: absolute;
        inset: -4px;
        border-radius: 32px;
        background: conic-gradient(
            from var(--glow-angle, 0deg),
            #a855f7,
            #6366f1,
            #06b6d4,
            #22d3ee,
            #a855f7,
            #ec4899,
            #a855f7
        );
        filter: blur(12px);
        opacity: 0.4;
        animation: glow-spin 3s linear infinite;
    }

    @keyframes glow-spin {
        to {
            --glow-angle: 360deg;
        }
    }

    @property --glow-angle {
        syntax: '<angle>';
        initial-value: 0deg;
        inherits: false;
    }

    .chat-textarea {
        flex: 1;
        padding: 6px 0;
        border: none;
        font-size: 0.95rem;
        font-family: inherit;
        outline: none;
        color: var(--text);
        background: transparent;
        resize: none;
        min-height: 32px;
        max-height: 200px;
        line-height: 1.6;
        scrollbar-width: none;
    }

    .chat-textarea::-webkit-scrollbar {
        display: none;
    }

    .chat-textarea::placeholder {
        color: var(--faint);
    }

    .chat-textarea:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .send-button {
        padding: 0;
        background: var(--panelSolid);
        color: var(--text);
        border: 0.36px solid var(--border);
        border-radius: 50%;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.06s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        flex-shrink: 0;
    }

    .send-icon {
        width: 18px;
        height: 18px;
    }

    .button-spinner {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }

    .send-button:hover:not(:disabled) {
        background: var(--bg0);
    }

    .send-button:active:not(:disabled) {
        transform: scale(0.95);
    }

    .send-button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
</style>
